import projectValue from '#calculator/projectValue'
import type { TaxCalculationResult } from '#calculator/types'
import { TaxKey, type TaxMetadata } from '#dictionary'
import { TAX_BY_KEY } from '#dictionary/taxes'
import {
  LATEST_SOCIAL_SECURITY_LIMIT,
  SOCIAL_SECURITY_LIMIT_BY_YEAR,
  type TaxSocialSecurityLimit,
} from '#dictionary/taxSocialSecurityLimits'

import type { ITaxCalculator, NoTaxVariety, TaxBracketBreakdown } from './types'

type SocialSecurityTaxKey =
  TaxKey.TaxSocialSecurity | TaxKey.TaxSocialSecuritySE

// TODO: @max May we treat Social Security Tax as a Marginal with rate zero over a certain income?
class SocialSecurityTax implements ITaxCalculator<NoTaxVariety> {
  private readonly metadata: TaxMetadata

  constructor(taxKey: SocialSecurityTaxKey) {
    this.metadata = TAX_BY_KEY.get(taxKey)!
  }

  public calculate(amount: number, year: number): TaxCalculationResult {
    const taxableLimit = this.getTaxableThreshold(year)
    const taxableIncome = Math.min(amount, taxableLimit)
    const taxRate = this.getTaxRate(year)
    const totalTax = taxableIncome * taxRate
    const descriptionPrefix = `Social Security Wage Base Limit ${year}`
    const brackets = []

    if (amount > 0) {
      const incomeInFirstBracket = Math.min(amount, taxableLimit)
      const taxInFirstBracket = incomeInFirstBracket * taxRate

      const firstBracket: TaxBracketBreakdown = {
        year: year,
        filingStatus: 'N/A',
        from: 0,
        to: taxableLimit,
        taxRate: taxRate,
        description: `${descriptionPrefix} - Income up to wage base limit`,
        incomeInBracket: incomeInFirstBracket,
        taxInBracket: taxInFirstBracket,
      }
      brackets.push(firstBracket)
    }

    if (amount > taxableLimit) {
      const secondBracket: TaxBracketBreakdown = {
        year: year,
        filingStatus: 'N/A',
        from: taxableLimit,
        to: amount,
        taxRate: 0,
        description: `${descriptionPrefix} - Income above wage base limit`,
        incomeInBracket: amount - taxableLimit,
        taxInBracket: 0,
      }
      brackets.push(secondBracket)
    }

    return {
      amount,
      taxMetadata: this.metadata,
      taxableIncome,
      effectiveRate: amount > 0 ? totalTax / amount : 0,
      totalTax,
      standardDeduction: 0,
      bracketBreakdown: brackets,
    }
  }

  public getTaxRate(year: number): number {
    return this.findLimitForYear(year).taxRate
  }

  public getTaxableThreshold(year: number): number {
    const baseLimit = this.findLimitForYear(year)

    return projectValue(
      baseLimit.limit,
      this.metadata.growthRate,
      baseLimit.year,
      year
    )
  }

  private findLimitForYear(year: number): TaxSocialSecurityLimit {
    return (
      SOCIAL_SECURITY_LIMIT_BY_YEAR.get(year) ?? LATEST_SOCIAL_SECURITY_LIMIT
    )
  }
}

export default SocialSecurityTax
